import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRBAC } from '@/contexts/RBACContext';
import { LottieLoading } from '@/components/ui/lottie-loading';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Ensures only authenticated users can access its children; shows a loading UI while authentication AND RBAC are resolving.
 * CRITICAL: Must wait for BOTH auth AND rbacReady to prevent flash of unauthorized content.
 *
 * The flash occurs because:
 * 1. Auth completes quickly (user is set)
 * 2. Children render and load cached data from localStorage
 * 3. RBAC loads and reveals user doesn't have access to cached content
 * 4. Content disappears (the "flash")
 *
 * By waiting for rbacReady, we ensure permissions are known BEFORE any content renders.
 *
 * @returns The React element: a full-screen loading UI while authentication/RBAC is loading, a redirect to `/login` with the current query string when unauthenticated, or the wrapped `children` when authorized.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { rbacReady } = useRBAC();
  const location = useLocation();

  // CRITICAL: Wait for BOTH auth AND RBAC to be ready
  // This prevents the flash of unauthorized content on page refresh
  if (authLoading || !rbacReady) {
    // No message - animation only. Map page has its own phased loading messages.
    return <LottieLoading />;
  }

  if (!user) {
    // Preserve query params when redirecting to login
    // Note: Demo mode is now detected by hostname, not URL params
    const loginPath = `/login${location.search}`;
    return <Navigate to={loginPath} replace />;
  }

  return <>{children}</>;
}